import { Loader2, Pause, Play, ThumbsDown, ThumbsUp, Volume, Volume1, Volume2, ArrowLeft } from 'lucide-react';
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

const IK_BASE = 'https://ik.imagekit.io/armppy';

const COLORS = {
    bg: '#0b0614',
    bg2: '#130b22',
    card: 'rgba(255,255,255,0.04)',
    border: 'rgba(168,85,247,0.16)',
    primary: '#a855f7',
    primary2: '#c084fc',
    text: '#f5f3ff',
    textSoft: 'rgba(245,243,255,0.7)',
    textMuted: 'rgba(245,243,255,0.38)',
};

const videos = [
    {
        id: 234,
        title: "Mountain View",
        description: 'A breathtaking journey through majestic mountains beneath an endless sky. Feel the serenity of nature, where every cloud, breeze, and sunrise tells a story of peace and wonder.',
        thumbnail: `${IK_BASE}/vootage1.mp4/ik-thumbnail.jpg`,
        src: `${IK_BASE}/vootage1.mp4`,
        likes: 2011,
        duration: '2:14',
        views: '18K',
        channel: 'NatureScope',
    },
    {
        id: 235,
        title: "Waterfalls",
        description: 'A mesmerizing waterfall flowing through the heart of nature, where the sound of rushing water and the beauty of untouched landscapes create a moment of pure tranquility.',
        thumbnail: `${IK_BASE}/vootage2.mp4/ik-thumbnail.jpg`,
        src: `${IK_BASE}/vootage2.mp4`,
        likes: 2011,
        duration: '1:48',
        views: '24K',
        channel: 'NatureScope',
    },
    {
        id: 1,
        title: 'Cinematic Human',
        description: 'A cinematic video showcasing skills in video editing, storytelling, and color grading.',
        thumbnail: `${IK_BASE}/411CA06D-6A4F-4925-96AE-7D874CE0DEBB.MP4/ik-thumbnail.jpg`,
        src: `${IK_BASE}/411CA06D-6A4F-4925-96AE-7D874CE0DEBB.MP4`,
        likes: 2400,
        duration: '3:21',
        views: '41K',
        channel: 'Cinematic',
    },
    {
        id: 22,
        title: 'Rocket Racoon',
        description: 'A fun and dynamic video featuring Rocket Racoon, demonstrating ability to create engaging content with popular characters.',
        thumbnail: `${IK_BASE}/snaptik_7469140081647881502_v3.mp4/ik-thumbnail.jpg`,
        src: `${IK_BASE}/snaptik_7469140081647881502_v3.mp4`,
        likes: 1800,
        duration: '0:58',
        views: '33K',
        channel: 'Rocket',
    },
    {
        id: 22487,
        title: 'The Ocean',
        description: 'A fun and dynamic video featuring Rocket Racoon, demonstrating ability to create engaging content with popular characters.',
        thumbnail: `${IK_BASE}/vootage3.mp4/ik-thumbnail.jpg`,
        src: `${IK_BASE}/vootage3.mp4`,
        likes: 2422,
        duration: '0:58',
        views: '33K',
        channel: 'The Ocean',
    },
    {
        id: 3234,
        title: 'The River',
        description: 'Where the river flows, peace follows. 🌊🌿✨',
        thumbnail: `${IK_BASE}/download.mp4/ik-thumbnail.jpg`,
        src: `${IK_BASE}/download.mp4`,
        likes: 2131,
        duration: '0:58',
        views: '33K',
        channel: 'The River',
    },
];

function fmtCount(n) {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(n);
}

function spawnBurst(btnEl, color) {
    for (let i = 0; i < 8; i++) {
        const d = document.createElement('div');
        const size = 7 + Math.random() * 9;
        const angle = (i / 8) * 360 + Math.random() * 15;
        const dist = 26 + Math.random() * 16;
        const x = Math.cos((angle * Math.PI) / 180) * dist;
        const y = Math.sin((angle * Math.PI) / 180) * dist;
        Object.assign(d.style, {
            position: 'absolute', width: `${size}px`, height: `${size}px`,
            borderRadius: '50%', background: color, left: '50%', top: '50%',
            marginLeft: `${-size / 2}px`, marginTop: `${-size / 2}px`,
            pointerEvents: 'none', zIndex: 9999,
        });
        btnEl.appendChild(d);
        d.animate(
            [
                { transform: 'translate(0,0) scale(0)', opacity: 0.85 },
                { transform: `translate(${x}px,${y}px) scale(1.1)`, opacity: 0.5, offset: 0.5 },
                { transform: `translate(${x * 1.7}px,${y * 1.7}px) scale(0)`, opacity: 0 },
            ],
            { duration: 500, easing: 'ease-out', fill: 'forwards' }
        ).onfinish = () => d.remove();
    }
}

function Spinner() {
    return (
        <div style={styles.spinnerWrap}>
            <div style={styles.spinnerGlass}>
                <Loader2 size={34} style={styles.spinnerIcon} />
            </div>
        </div>
    );
}

function AmbientCanvas({ videoRef }) {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const sample = () => {
            const video = videoRef.current;
            if (!video || video.readyState < 2 || video.paused || video.ended) {
                rafRef.current = requestAnimationFrame(sample); return;
            }
            try { ctx.drawImage(video, 0, 0, canvas.width, canvas.height); } catch (_) { }
            rafRef.current = requestAnimationFrame(sample);
        };
        rafRef.current = requestAnimationFrame(sample);
        return () => cancelAnimationFrame(rafRef.current);
    }, [videoRef]);
    return <canvas ref={canvasRef} width={32} height={18} style={styles.ambientCanvas} />;
}

function VolumeControl({ volume, muted, onToggleMute, onVolumeChange }) {
    const [hovered, setHovered] = useState(false);
    const icon = muted || volume === 0 ? <Volume size={18} /> : volume < 0.4 ? <Volume1 size={18} /> : <Volume2 size={18} />;
    return (
        <div style={styles.volumeWrap} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
            <button style={styles.iconBtn} onClick={onToggleMute}>{icon}</button>
            <div style={{ ...styles.volumeSliderWrap, width: hovered ? '80px' : '0px', opacity: hovered ? 1 : 0 }}>
                <div style={styles.volumeTrack}>
                    <div style={{ ...styles.volumeFill, width: `${muted ? 0 : volume * 100}%` }} />
                    <div style={{ ...styles.volumeThumb, left: `${muted ? 0 : volume * 100}%` }} />
                    <input type="range" min={0} max={1} step={0.02} value={muted ? 0 : volume}
                        onChange={(e) => onVolumeChange(parseFloat(e.target.value))} style={styles.volumeInput} />
                </div>
            </div>
        </div>
    );
}

function EndOverlay({ active, onReplay, onNext, countdown }) {
    const currentIndex = videos.findIndex((v) => v.id === active.id);
    const nextVideo = videos[currentIndex + 1] || videos[0];
    const isLast = currentIndex === videos.length - 1;
    return (
        <div style={styles.endOverlay}>
            <div style={styles.endContent}>
                <div style={styles.endButtons}>
                    <button style={styles.endBtn} onClick={onReplay}>
                        <span style={styles.endBtnIcon}>↺</span><span>Replay</span>
                    </button>
                    <button style={{ ...styles.endBtn, ...styles.endBtnPrimary }} onClick={() => onNext(nextVideo)}>
                        <div style={styles.endNextThumb}>
                            <img src={nextVideo.thumbnail} alt={nextVideo.title} style={styles.endNextImg} />
                        </div>
                        <p style={styles.endLabel}>Next video in {countdown}s</p>
                        <div style={styles.endNextInfo}>
                            <span style={styles.endNextLabel}>{isLast ? 'Back to first' : 'Next up'}</span>
                            <span style={styles.endNextTitle}>{nextVideo.title}</span>
                        </div>
                        <span style={styles.endBtnIcon}><Play size={18} /></span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function ReactionBar({ videoId }) {
    const [liked, setLiked] = useState(false);
    const [disliked, setDisliked] = useState(false);
    const [count, setCount] = useState(videos.find((v) => v.id === videoId)?.likes ?? 0);
    useEffect(() => {
        setLiked(false); setDisliked(false);
        setCount(videos.find((v) => v.id === videoId)?.likes ?? 0);
    }, [videoId]);
    const likeBtnRef = useRef(null);
    const dislikeBtnRef = useRef(null);
    const handleLike = () => {
        if (!liked) {
            if (disliked) setDisliked(false);
            setLiked(true); setCount((c) => c + 1);
            if (likeBtnRef.current) spawnBurst(likeBtnRef.current, '#a855f7');
        } else { setLiked(false); setCount((c) => c - 1); }
    };
    const handleDislike = () => {
        if (!disliked) {
            if (liked) { setLiked(false); setCount((c) => c - 1); }
            setDisliked(true);
            if (dislikeBtnRef.current) spawnBurst(dislikeBtnRef.current, '#818cf8');
        } else { setDisliked(false); }
    };
    return (
        <div style={styles.reactionBar}>
            <button ref={likeBtnRef} style={{ ...styles.reactBtn, ...(liked ? styles.reactBtnLiked : {}), overflow: 'hidden', position: 'relative' }} onClick={handleLike}>
                <ThumbsUp size={18} style={{ transition: 'transform 0.3s cubic-bezier(.34,1.56,.64,1)', transform: liked ? 'scale(1.35)' : 'scale(1)' }} />
                <span style={{ minWidth: '32px', transition: 'color 0.2s' }}>{fmtCount(count)}</span>
            </button>
            <div style={styles.reactionDivider} />
            <button ref={dislikeBtnRef} style={{ ...styles.reactBtn, ...(disliked ? styles.reactBtnDisliked : {}), overflow: 'hidden', position: 'relative', paddingLeft: '14px', paddingRight: '14px' }} onClick={handleDislike}>
                <ThumbsDown size={18} style={{ transition: 'transform 0.3s cubic-bezier(.34,1.56,.64,1)', transform: disliked ? 'scale(1.35)' : 'scale(1)' }} />
            </button>
        </div>
    );
}


const colorCache = {};
function getDominantColor(imgEl) {
    const canvas = document.createElement('canvas');
    canvas.width = 16; canvas.height = 9;
    const ctx = canvas.getContext('2d');
    try {
        ctx.drawImage(imgEl, 0, 0, 16, 9);
        const data = ctx.getImageData(0, 0, 16, 9).data;
        let r = 0, g = 0, b = 0, count = 0;
        for (let i = 0; i < data.length; i += 4) {
            const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
            if (brightness > 30 && brightness < 240) { r += data[i]; g += data[i + 1]; b += data[i + 2]; count++; }
        }
        if (!count) return `168,85,247`;
        r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count);
        if (Math.max(r, g, b) - Math.min(r, g, b) < 30) { const s = 180 / Math.max(r, g, b); r = Math.round(r * s); g = Math.round(g * s); b = Math.round(b * s); }
        return `${r},${g},${b}`;
    } catch (e) { return `168,85,247`; }
}


/* ─── Grid Home Screen ───────────────────────────────────── */
function HomeGrid({ onPlay }) {
    const [hoveredId, setHoveredId] = useState(null);
    const [hoverColors, setHoverColors] = useState({});
    const imgRefs = useRef({});

    const handleImgLoad = (id, el) => {
        el.crossOrigin = 'anonymous';
        const color = getDominantColor(el);
        colorCache[id] = color;
        setHoverColors(prev => ({ ...prev, [id]: color }));
    };

    return (
        <div style={styles.page}>
            <header style={styles.topbar}>
                <img alt="IkiTube" src="https://ik.imagekit.io/armppy/ikitube-removebg-preview.png" style={{ height: 40 }} />
                <div style={styles.logo}>ikiTube</div>
            </header>
            <div style={styles.homeContent}>
                <div style={styles.homeGrid}>
                    {videos.map((v) => {
                        const isHovered = hoveredId === v.id;
                        const c = hoverColors[v.id] || '168,85,247';
                        return (
                            <button
                                key={v.id}
                                style={{
                                    ...styles.gridCard,
                                    boxShadow: isHovered
                                        ? `0 0 0 1px rgba(${c},0.12), 0 8px 40px rgba(${c},0.35)`
                                        : 'none',
                                    transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
                                    transition: 'transform 0.22s ease, border-color 0.3s ease, box-shadow 0.3s ease',
                                }}
                                onClick={() => { onPlay(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                onMouseEnter={() => setHoveredId(v.id)}
                                onMouseLeave={() => setHoveredId(null)}
                            >
                                <div style={styles.gridThumbWrap}>
                                    <img
                                        src={v.thumbnail}
                                        alt={v.title}
                                        crossOrigin="anonymous"
                                        ref={el => { if (el) imgRefs.current[v.id] = el; }}
                                        onLoad={(e) => handleImgLoad(v.id, e.currentTarget)}
                                        style={{ ...styles.gridThumb, transform: isHovered ? 'scale(1.04)' : 'scale(1)' }}
                                    />
                                    <div style={{ ...styles.gridPlayOverlay, opacity: isHovered ? 1 : 0 }}>
                                        <div style={{
                                            ...styles.gridPlayBtn,
                                            background: isHovered ? `rgba(${c},0.85)` : 'rgba(168,85,247,0.85)',
                                        }}>
                                            <Play size={20} fill="white" color="white" />
                                        </div>
                                    </div>
                                    <div style={styles.gridDuration}>{v.duration}</div>
                                </div>
                                <div style={styles.gridInfo}>
                                    <div style={{
                                        ...styles.gridAvatar,
                                        background: isHovered
                                            ? `linear-gradient(135deg, rgba(${c},0.9), rgba(${c},0.6))`
                                            : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                                        transition: 'background 0.3s ease',
                                    }}>
                                        {v.channel.charAt(0)}
                                    </div>
                                    <div style={styles.gridMeta}>
                                        <div style={styles.gridTitle}>{v.title}</div>
                                        <div style={styles.gridChannel}>{v.channel}</div>
                                        <div style={styles.gridStats}>{v.views} views</div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/* ─── Player Screen ──────────────────────────────────────── */
function PlayerScreen({ initialVideo, onBack }) {
    const [active, setActive] = useState(initialVideo);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [buffered, setBuffered] = useState(0);
    const [currentTime, setCurrentTime] = useState('0:00');
    const [duration, setDuration] = useState('0:00');
    const [showControls, setShowControls] = useState(true);
    const [ended, setEnded] = useState(false);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [loadState, setLoadState] = useState('loading');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [countdown, setCountdown] = useState(5);
    const countdownRef = useRef(null);
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const hideTimeoutRef = useRef(null);
    const playingRef = useRef(playing);
    const [thumbColor, setThumbColor] = useState('124,58,237');

    useEffect(() => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = active.thumbnail;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = 16; canvas.height = 9;
            const ctx = canvas.getContext('2d');
            try {
                ctx.drawImage(img, 0, 0, 16, 9);
                const data = ctx.getImageData(0, 0, 16, 9).data;
                let r = 0, g = 0, b = 0, count = 0;
                for (let i = 0; i < data.length; i += 4) {
                    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
                    if (brightness > 30 && brightness < 240) { r += data[i]; g += data[i + 1]; b += data[i + 2]; count++; }
                }
                if (!count) return;
                r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count);
                if (Math.max(r, g, b) - Math.min(r, g, b) < 30) {
                    const s = 180 / Math.max(r, g, b);
                    r = Math.round(r * s); g = Math.round(g * s); b = Math.round(b * s);
                }
                setThumbColor(`${r},${g},${b}`);
            } catch (e) { }
        };
    }, [active.thumbnail]);

    useEffect(() => { playingRef.current = playing; }, [playing]);

    useEffect(() => {
        const resize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, []);

    useEffect(() => {
        if (!videoRef.current) return;
        videoRef.current.load();
        setPlaying(false); setProgress(0); setBuffered(0);
        setCurrentTime('0:00'); setDuration('0:00'); setEnded(false); setLoadState('loading');
    }, [active.src]);

    useEffect(() => {
        if (!videoRef.current) return;
        videoRef.current.volume = volume; videoRef.current.muted = muted;
    }, [volume, muted]);

    // Auto-play on mount
    useEffect(() => {
        const tryPlay = async () => {
            if (videoRef.current) {
                try {
                    await videoRef.current.play();
                    setPlaying(true); setLoadState('ready');
                } catch (_) { setLoadState('ready'); }
            }
        };
        const t = setTimeout(tryPlay, 300);
        return () => clearTimeout(t);
    }, []);

    const showOverlay = useCallback(() => {
        setShowControls(true);
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => {
            if (playingRef.current) setShowControls(false);
        }, 2000);
    }, []);

    useEffect(() => {
        showOverlay();
        return () => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); };
    }, [playing, showOverlay]);

    const formatTime = (time) => {
        if (!time || isNaN(time)) return '0:00';
        const mins = Math.floor(time / 60);
        const secs = Math.floor(time % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };

    const togglePlay = useCallback(async () => {
        if (!videoRef.current || loadState === 'loading') return;
        try {
            if (playingRef.current) {
                videoRef.current.pause(); setPlaying(false);
            } else {
                if (videoRef.current.ended) { videoRef.current.currentTime = 0; setEnded(false); }
                setLoadState('buffering');
                await videoRef.current.play();
                setPlaying(true); setLoadState('ready');
            }
            showOverlay();
        } catch (err) { setLoadState('ready'); }
    }, [showOverlay, loadState]);

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        setProgress((video.currentTime / video.duration) * 100);
        setCurrentTime(formatTime(video.currentTime));
        if (video.buffered.length > 0) {
            setBuffered((video.buffered.end(video.buffered.length - 1) / video.duration) * 100);
        }
    };

    const handleLoadedMetadata = () => {
        if (!videoRef.current) return;
        setDuration(formatTime(videoRef.current.duration)); setLoadState('ready');
    };

    const handleCanPlay = () => { if (loadState === 'buffering' || loadState === 'loading') setLoadState('ready'); };
    const handleWaiting = () => { if (playingRef.current) setLoadState('buffering'); };

    const handleEnded = () => {
        setPlaying(false); setEnded(true); setShowControls(true); setLoadState('ready');
        let timeLeft = 5; setCountdown(timeLeft);
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
            timeLeft -= 1; setCountdown(timeLeft);
            if (timeLeft <= 0) {
                clearInterval(countdownRef.current);
                const currentIndex = videos.findIndex((v) => v.id === active.id);
                handleNext(videos[currentIndex + 1] || videos[0]);
            }
        }, 1000);
    };

    const handleSeek = (e) => {
        if (!videoRef.current) return;
        const rect = e.currentTarget.getBoundingClientRect();
        videoRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * videoRef.current.duration;
        if (ended) setEnded(false);
    };

    const handleReplay = async () => {
        if (!videoRef.current) return;
        if (countdownRef.current) clearInterval(countdownRef.current);
        videoRef.current.currentTime = 0;
        try { await videoRef.current.play(); setPlaying(true); setEnded(false); setProgress(0); setLoadState('ready'); } catch (_) { }
    };

    const handleNext = async (nextVideo) => {
        if (countdownRef.current) clearInterval(countdownRef.current);
        setActive(nextVideo); setEnded(false); setLoadState('loading');
        setTimeout(async () => {
            try { if (videoRef.current) { await videoRef.current.play(); setPlaying(true); setLoadState('ready'); } } catch (_) { }
        }, 250);
    };

    const toggleFullscreen = async () => {
        const video = videoRef.current; const player = playerRef.current;
        if (!video || !player) return;
        if (video.webkitEnterFullscreen) {
            if (document.webkitFullscreenElement || document.fullscreenElement) {
                if (document.webkitExitFullscreen) document.webkitExitFullscreen();
                else if (document.exitFullscreen) document.exitFullscreen();
            } else { video.webkitEnterFullscreen(); }
            return;
        }
        if (!document.fullscreenElement) await player.requestFullscreen();
        else await document.exitFullscreen();
    };

    useEffect(() => { return () => { if (countdownRef.current) clearInterval(countdownRef.current); }; }, []);

    const isSpinning = loadState === 'loading' || loadState === 'buffering';

    return (
        <div style={styles.page}>
            <header style={styles.topbar}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button style={styles.backBtn} onClick={onBack}>
                        <ArrowLeft size={18} />
                    </button>
                    <img alt="IkiTube" src="https://ik.imagekit.io/armppy/ikitube-removebg-preview.png" style={{ height: 40 }} />
                    <div style={styles.logo}>ikiTube</div>
                </div>
            </header>

            <div style={{ ...styles.layout, ...(isMobile && styles.layoutMobile) }}>
                {/* LEFT */}
                <div style={styles.mainContent}>
                    <div
                        ref={playerRef}
                        style={{
                            ...styles.videoWrap,
                            boxShadow: `0 20px 80px rgba(${thumbColor},0.35), 0 0 0 1px rgba(${thumbColor},0.15)`,
                            transition: 'box-shadow 0.6s ease, border-color 0.6s ease',
                        }}
                        onMouseMove={showOverlay}
                        onTouchStart={showOverlay}
                    >
                        <AmbientCanvas videoRef={videoRef} />
                        <video ref={videoRef} src={active.src} poster={active.thumbnail} style={styles.video}
                            playsInline preload="metadata"
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={handleLoadedMetadata}
                            onCanPlay={handleCanPlay}
                            onCanPlayThrough={() => setLoadState('ready')}
                            onWaiting={handleWaiting}
                            onEnded={handleEnded}
                        />
                        {isSpinning && !ended && <div style={styles.spinnerOverlay}><Spinner /></div>}
                        {ended && <EndOverlay active={active} onReplay={handleReplay} onNext={handleNext} countdown={countdown} />}
                        {showControls && !ended && !isSpinning && (
                            <button style={styles.centerPlay} onClick={togglePlay}>
                                {playing ? <Pause size={18} /> : <Play size={18} />}
                            </button>
                        )}
                        <div style={{ ...styles.controls, opacity: showControls && !ended ? 1 : 0, pointerEvents: ended ? 'none' : 'auto' }}>
                            <div style={styles.progressWrap} onClick={handleSeek}>
                                <div style={{ ...styles.bufferedBar, width: `${buffered}%` }} />
                                <div style={{ ...styles.progressBar, width: `${progress}%` }} />
                                <div style={{ ...styles.progressThumb, left: `${progress}%` }} />
                            </div>
                            <div style={styles.controlsRow}>
                                <div style={styles.controlsLeft}>
                                    <button style={{ ...styles.iconBtn, opacity: loadState === 'loading' ? 0.4 : 1, cursor: loadState === 'loading' ? 'not-allowed' : 'pointer' }} onClick={togglePlay} disabled={loadState === 'loading'}>
                                        {isSpinning ? <Loader2 size={18} /> : playing ? <Pause size={18} /> : <Play size={18} />}
                                    </button>
                                    <VolumeControl volume={volume} muted={muted} onToggleMute={() => setMuted(p => !p)} onVolumeChange={(val) => { setVolume(val); if (val > 0) setMuted(false); }} />
                                    <span style={styles.time}>{currentTime} / {duration}</span>
                                </div>
                                <button style={styles.iconBtn} onClick={toggleFullscreen}>⛶</button>
                            </div>
                        </div>
                    </div>

                    <div style={styles.meta}>
                        <h1 style={styles.title}>{active.title}</h1>
                        <ReactionBar videoId={active.id} />
                        <p style={styles.description}>{active.description}</p>
                    </div>
                </div>

                {/* SIDEBAR */}
                <aside style={{ ...styles.sidebar, ...(isMobile && styles.sidebarMobile) }}>
                    <div style={styles.playlist}>
                        {videos.map((v) => (
                            <button key={v.id}
                                onClick={() => { window.scrollTo(0, 0); setActive(v); setEnded(false); setTimeout(async () => { setPlaying(true); await videoRef.current?.play(); }, 200); }}
                                style={{ ...styles.playlistItem, ...(active.id === v.id ? styles.playlistItemActive : {}) }}
                            >
                                <div style={styles.thumbWrap}>
                                    <img src={v.thumbnail} alt={v.title} style={styles.thumb} />
                                    {active.id === v.id && playing && <div style={styles.thumbPlaying}><Play size={18} /></div>}
                                    <div style={styles.thumbDuration}>{v.duration}</div>
                                </div>
                                <div style={styles.videoInfo}>
                                    <div style={styles.videoTitle}>{v.title}</div>
                                    <div style={styles.videoDesc}>{v.description}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
}

/* ─── Root App ───────────────────────────────────────────── */
export default function App({ onBack }) {
    const [selectedVideo, setSelectedVideo] = useState(null);

    if (selectedVideo) {
        return <PlayerScreen initialVideo={selectedVideo} onBack={() => setSelectedVideo(null)} />;
    }

    return <HomeGrid onPlay={(v) => setSelectedVideo(v)} />;
}

/* ─── Styles ─────────────────────────────────────────────── */
const styles = {
    page: {
        minHeight: '100vh',
        background: '   #0F0F0F',
        color: COLORS.text,
        fontFamily: "'Inter', sans-serif",
    },
    topbar: {
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        borderBottom: `1px solid ${COLORS.border}`,
        backdropFilter: 'blur(20px)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: '#0F0F0F',
    },
    logo: {
        fontWeight: 900,
        letterSpacing: '0.2em',
        color: COLORS.primary2,
        fontSize: '18px',
    },
    backBtn: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        border: `1px solid ${COLORS.border}`,
        background: 'rgba(255,255,255,0.06)',
        color: COLORS.text,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.2s',
    },

    /* ── Home Grid ── */
    homeContent: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '32px 24px',
    },
    homeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px',
    },
    gridCard: {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        textAlign: 'left',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'transform 0.2s ease',
    },
    gridThumbWrap: {
        position: 'relative',
        width: '100%',
        aspectRatio: '16/9',
        borderRadius: '12px',
        overflow: 'hidden',
        background: COLORS.bg2,
    },
    gridThumb: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        transition: 'transform 0.3s ease',
    },
    gridPlayOverlay: {
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.2s ease',
    },
    gridPlayBtn: {
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        background: 'rgba(168,85,247,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(8px)',
    },
    gridDuration: {
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        background: 'rgba(0,0,0,0.8)',
        color: '#fff',
        fontSize: '12px',
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: '4px',
        letterSpacing: '0.03em',
    },
    gridInfo: {
        display: 'flex',
        gap: '12px',
        padding: '12px 4px 8px',
        alignItems: 'flex-start',
    },
    gridAvatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 700,
        color: '#fff',
        flexShrink: 0,
    },
    gridMeta: {
        flex: 1,
        minWidth: 0,
    },
    gridTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: COLORS.text,
        lineHeight: 1.4,
        marginBottom: '4px',
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    gridChannel: {
        fontSize: '12px',
        color: COLORS.textMuted,
        marginBottom: '2px',
    },
    gridStats: {
        fontSize: '12px',
        color: COLORS.textMuted,
    },

    /* ── Player Layout ── */
    layout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 380px',
        gap: '28px',
        padding: '28px',
        maxWidth: '1800px',
        margin: '0 auto',
    },
    layoutMobile: { display: 'flex', flexDirection: 'column', padding: '12px' },
    mainContent: { minWidth: 0 },
    videoWrap: {
        position: 'relative',
        width: '100%',
        aspectRatio: '16/9',
        borderRadius: '16px',
        overflow: 'hidden',
        background: COLORS.bg2,
    },
    ambientCanvas: {
        position: 'absolute', inset: '-15%', width: '130%', height: '130%',
        objectFit: 'cover', filter: 'blur(80px) saturate(2.2) brightness(0.9)',
        opacity: 0.55, transform: 'scale(1.1)', pointerEvents: 'none', zIndex: 1, imageRendering: 'pixelated',
    },
    video: { position: 'relative', zIndex: 2, width: '100%', height: '100%', objectFit: 'cover' },
    spinnerOverlay: {
        position: 'absolute', inset: 0, zIndex: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(5,1,14,0.45)', backdropFilter: 'blur(2px)',
    },
    spinnerWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    spinnerGlass: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px',
        padding: '10px', borderRadius: '99px', background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
    },
    spinnerIcon: { color: COLORS.primary2, animation: 'spin 1s linear infinite' },
    endOverlay: {
        position: 'absolute', inset: 0, zIndex: 10,
        background: 'rgba(5,1,14,0.82)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    endContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' },
    endLabel: { margin: 0, fontSize: '13px', color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' },
    endButtons: { display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' },
    endBtn: {
        display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 20px',
        borderRadius: '14px', border: `1px solid ${COLORS.border}`, background: 'rgba(255,255,255,0.06)',
        color: COLORS.text, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
        backdropFilter: 'blur(12px)', transition: 'all 0.2s ease',
    },
    endBtnPrimary: {
        background: 'linear-gradient(135deg, rgba(168,85,247,0.25), rgba(192,132,252,0.12))',
        border: '1px solid rgba(168,85,247,0.4)', maxWidth: '280px',
    },
    endBtnIcon: { fontSize: '18px', color: COLORS.primary2 },
    endNextThumb: { width: '72px', height: '40px', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 },
    endNextImg: { width: '100%', height: '100%', objectFit: 'cover' },
    endNextInfo: { display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'left' },
    endNextLabel: { fontSize: '11px', color: COLORS.primary2, textTransform: 'uppercase', letterSpacing: '0.06em' },
    endNextTitle: { fontSize: '13px', color: COLORS.text, fontWeight: 600, lineHeight: 1.3 },
    centerPlay: {
        position: 'absolute', zIndex: 5, top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '50px', height: '50px', borderRadius: '999px', border: `1px solid ${COLORS.border}`,
        background: 'rgba(10,10,10,0.5)', backdropFilter: 'blur(20px)', color: COLORS.primary2,
        fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    controls: {
        position: 'absolute', zIndex: 6, left: 0, right: 0, bottom: 0, padding: '18px',
        transition: 'opacity 0.35s ease', background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
    },
    progressWrap: {
        position: 'relative', width: '100%', height: '5px', borderRadius: '999px',
        overflow: 'visible', background: 'rgba(255,255,255,0.1)', cursor: 'pointer', marginBottom: '16px',
    },
    bufferedBar: { position: 'absolute', top: 0, left: 0, height: '5px', borderRadius: '999px', background: 'rgba(255,255,255,0.25)' },
    progressBar: { position: 'absolute', top: 0, left: 0, height: '5px', borderRadius: '999px', background: 'linear-gradient(90deg,#7c3aed,#c084fc)' },
    progressThumb: { position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)', width: '13px', height: '13px', borderRadius: '50%', background: '#c084fc', boxShadow: '0 0 6px rgba(192,132,252,0.8)', pointerEvents: 'none' },
    controlsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    controlsLeft: { display: 'flex', alignItems: 'center', gap: '8px' },
    iconBtn: {
        width: '38px', height: '38px', borderRadius: '999px', border: 'none', cursor: 'pointer',
        background: 'rgba(255,255,255,0.08)', color: '#fff', backdropFilter: 'blur(10px)',
        flexShrink: 0, fontSize: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', transition: 'opacity 0.2s ease',
    },
    time: { fontSize: '13px', color: COLORS.textSoft, whiteSpace: 'nowrap' },
    volumeWrap: { display: 'flex', alignItems: 'center', gap: '6px' },
    volumeSliderWrap: { overflow: 'hidden', transition: 'width 0.25s ease, opacity 0.25s ease', display: 'flex', alignItems: 'center' },
    volumeTrack: { position: 'relative', width: '72px', height: '4px', borderRadius: '999px', background: 'rgba(255,255,255,0.15)', cursor: 'pointer' },
    volumeFill: { position: 'absolute', top: 0, left: 0, height: '4px', borderRadius: '999px', background: 'linear-gradient(90deg,#7c3aed,#c084fc)', pointerEvents: 'none' },
    volumeThumb: { position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)', width: '11px', height: '11px', borderRadius: '50%', background: '#c084fc', pointerEvents: 'none' },
    volumeInput: { position: 'absolute', inset: '-8px 0', width: '100%', opacity: 0, cursor: 'pointer', margin: 0 },
    meta: { marginTop: '22px' },
    title: { margin: 0, fontSize: '28px', fontWeight: 700, lineHeight: 1.2 },
    reactionBar: {
        display: 'flex', alignItems: 'center', marginTop: '18px', marginBottom: '4px',
        background: 'rgba(255,255,255,0.04)', border: `1px solid ${COLORS.border}`,
        borderRadius: '999px', width: 'fit-content', overflow: 'hidden',
    },
    reactionDivider: { width: '1px', height: '24px', background: COLORS.border, flexShrink: 0 },
    reactBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', border: 'none', background: 'transparent', color: COLORS.textSoft, fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s, color 0.2s' },
    reactBtnLiked: { color: COLORS.primary2, background: 'rgba(168,85,247,0.12)' },
    reactBtnDisliked: { color: '#818cf8', background: 'rgba(99,102,241,0.12)' },
    description: { marginTop: '14px', color: COLORS.textSoft, lineHeight: 1.7, maxWidth: '900px' },
    sidebar: { height: 'fit-content', position: 'sticky', top: '80px' },
    sidebarMobile: { position: 'relative', top: 0 },
    playlist: { display: 'flex', flexDirection: 'column', gap: '12px' },
    playlistItem: {
        display: 'flex', gap: '12px', padding: '10px', borderRadius: '18px',
        background: 'rgba(255,255,255,0.03)', border: '1px solid transparent',
        cursor: 'pointer', transition: 'all 0.25s ease', textAlign: 'left',
    },
    playlistItemActive: { border: `1px solid ${COLORS.border}`, background: 'linear-gradient(135deg, rgba(168,85,247,0.15), rgba(255,255,255,0.03))' },
    thumbWrap: { position: 'relative', width: '160px', height: '90px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 },
    thumbPlaying: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: COLORS.primary2 },
    thumbDuration: { position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '1px 5px', borderRadius: '3px' },
    thumb: { width: '100%', height: '100%', objectFit: 'cover' },
    videoInfo: { display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 },
    videoTitle: { fontSize: '14px', fontWeight: 600, color: COLORS.text, lineHeight: 1.4 },
    videoDesc: { fontSize: '12px', color: COLORS.textMuted, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' },
};

const styleTag = document.createElement('style');
styleTag.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleTag);